import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { CommissionTransaction } from '../../types';

const AdminCommissions: React.FC = () => {
  const [commissions, setCommissions] = useState<CommissionTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [, setTotalPages] = useState(1);

  useEffect(() => {
    loadCommissions();
  }, [page, statusFilter]);

  const loadCommissions = async () => {
    setLoading(true);
    const response = await api.getAllCommissions({
      page, pageSize: 20,
      status: statusFilter !== 'all' ? statusFilter : undefined
    });
    if (response.success && response.data) {
      setCommissions(response.data.data);
      setTotalPages(response.data.totalPages);
    }
    setLoading(false);
  };

  const handleApprove = async (id: string) => {
    const response = await api.approveCommission(id);
    if (response.success) loadCommissions();
    else alert(response.error);
  };

  const handleMarkPaid = async (id: string) => {
    const ref = prompt('Enter payout reference (bKash TXN, bank ref):');
    if (!ref) return;
    const response = await api.markCommissionPaid(id, ref);
    if (response.success) loadCommissions();
    else alert(response.error);
  };

  const pendingTotal = commissions.filter(c => c.status === 'pending').reduce((s, c) => s + c.commissionAmount, 0);
  const approvedTotal = commissions.filter(c => c.status === 'approved').reduce((s, c) => s + c.commissionAmount, 0);
  const paidTotal = commissions.filter(c => c.status === 'paid').reduce((s, c) => s + c.commissionAmount, 0);

  if (loading && commissions.length === 0) {
    return <div className="flex items-center justify-center h-64"><div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Commission Management</h1>
        <p className="text-neutral-500">Manage reseller commission payouts</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
          <p className="text-neutral-400 text-sm">Pending Approval</p>
          <p className="text-2xl font-bold text-yellow-400">৳{pendingTotal.toLocaleString()}</p>
        </div>
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
          <p className="text-neutral-400 text-sm">Ready for Payout</p>
          <p className="text-2xl font-bold text-blue-400">৳{approvedTotal.toLocaleString()}</p>
        </div>
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
          <p className="text-neutral-400 text-sm">Total Paid</p>
          <p className="text-2xl font-bold text-emerald-400">৳{paidTotal.toLocaleString()}</p>
        </div>
      </div>

      <div className="flex gap-2">
        {['all', 'pending', 'approved', 'paid'].map(status => (
          <button key={status} onClick={() => { setStatusFilter(status); setPage(1); }}
            className={`px-4 py-2 rounded-xl text-sm font-medium ${statusFilter === status ? 'bg-emerald-600 text-white' : 'bg-[#1a1a1a] text-neutral-400 hover:bg-[#262626]'}`}>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      <div className="bg-[#121212] border border-[#262626] rounded-2xl overflow-hidden">
        {commissions.length > 0 ? (
          <table className="w-full">
            <thead className="bg-[#1a1a1a]">
              <tr>
                <th className="text-left text-xs text-neutral-500 uppercase p-4">Reseller</th>
                <th className="text-left text-xs text-neutral-500 uppercase p-4">Client</th>
                <th className="text-left text-xs text-neutral-500 uppercase p-4">Amount</th>
                <th className="text-left text-xs text-neutral-500 uppercase p-4">Commission</th>
                <th className="text-left text-xs text-neutral-500 uppercase p-4">Status</th>
                <th className="text-left text-xs text-neutral-500 uppercase p-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#262626]">
              {commissions.map(c => (
                <tr key={c.id} className="hover:bg-[#1a1a1a]">
                  <td className="p-4">
                    <div>
                      <p className="text-white font-medium">{c.reseller?.companyName || c.reseller?.email || 'Unknown'}</p>
                      <p className="text-neutral-500 text-xs">{c.reseller?.email}</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <div>
                      <p className="text-neutral-300">{c.client?.companyName || c.client?.email || 'Unknown'}</p>
                      <p className="text-neutral-500 text-xs">{c.client?.email}</p>
                    </div>
                  </td>
                  <td className="p-4 text-neutral-400">৳{c.subscriptionAmount.toLocaleString()}</td>
                  <td className="p-4">
                    <div>
                      <p className="text-emerald-400 font-bold">৳{c.commissionAmount.toLocaleString()}</p>
                      <p className="text-neutral-500 text-xs">{c.commissionRate}% rate</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                      c.status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' :
                      c.status === 'approved' ? 'bg-blue-500/20 text-blue-400' : 'bg-yellow-500/20 text-yellow-400'
                    }`}>{c.status.toUpperCase()}</span>
                    {c.payoutReference && (
                      <p className="text-neutral-500 text-xs mt-1">{c.payoutReference}</p>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      {c.status === 'pending' && (
                        <button onClick={() => handleApprove(c.id)} className="px-3 py-1 bg-blue-500/10 text-blue-400 rounded-lg text-xs hover:bg-blue-500/20 transition-colors">Approve</button>
                      )}
                      {c.status === 'approved' && (
                        <button onClick={() => handleMarkPaid(c.id)} className="px-3 py-1 bg-emerald-500/10 text-emerald-400 rounded-lg text-xs hover:bg-emerald-500/20 transition-colors">Mark Paid</button>
                      )}
                      {c.status === 'paid' && (
                        <span className="text-neutral-500 text-xs">Completed</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-8 text-center text-neutral-500">No commissions found</div>
        )}
      </div>
    </div>
  );
};

export default AdminCommissions;
